import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { TeamStore as MobxTeamStore } from 'nomst/Stores/team.store';
import { Dropdown } from '@payscale/design';
import { featureFlagHelper } from 'singletons';
import { Roles, FeatureFlags, PageTabs } from 'typings/enums';
import { contactFormHelper, navigateToNoMst } from 'utils/MobxDataManager/singletons';
import { configurationsStore, notificationStore } from 'utils/MobxDataManager/singletons';
import './headerNav.scss';

export interface IHeaderNavProps {
    store?: MobxTeamStore;
    isFullPageHelpForm?: boolean;
}

export class HeaderNav extends React.Component<IHeaderNavProps> {
    private readonly showUnificationTabs: boolean =
        featureFlagHelper?.featureFlagSettings[FeatureFlags.ShowUnificationTabs] as boolean;
    private readonly showContactForm: boolean =
        featureFlagHelper?.featureFlagSettings[FeatureFlags.ShowContactForm] as boolean;
    private readonly teamPayJobsList: boolean =
        featureFlagHelper?.featureFlagSettings[FeatureFlags.TeamPayJobsList] as boolean;

    constructor(props: IHeaderNavProps) {
        super(props);
        this.toggleNotificationMenu = this.toggleNotificationMenu.bind(this);
        this.goToDiagnosticsPage = this.goToDiagnosticsPage.bind(this);
        this.toggleSlideout = this.toggleSlideout.bind(this);
        this.handleKeyPress = this.handleKeyPress.bind(this);
        this.goToOverview = this.goToOverview.bind(this);
        this.goToCycleConfiguration = this.goToCycleConfiguration.bind(this);
        this.logout = this.logout.bind(this);
        this.goToGlobalConfiguration = this.goToGlobalConfiguration.bind(this);
        this.goToTeams = this.goToTeams.bind(this);
        this.goToLandingPage = this.goToLandingPage.bind(this);
        this.goToJobs = this.goToJobs.bind(this);
        this.goToEmployees = this.goToEmployees.bind(this);
    }

    _showOverviewTab(store: MobxTeamStore): boolean {
        return store.showMenuForRoles(Roles.HumanResource, Roles.ManagerOfManager, Roles.Support);
    }

    _showTeamsTab(store: MobxTeamStore): boolean {
        const isHr = store.user.hasHrRole;
        const isHrbp = store.user.isHrbp;
        const hasDirects = store.user.hasDirectReports;
        if ((isHr && !hasDirects) || (isHrbp && !hasDirects) || !store.masterAccountId) {
            return false;
        }

        if (!configurationsStore.currentConvertedConfiguration ||
            configurationsStore.currentConvertedConfiguration.cycleId === 0
            || configurationsStore.currentConvertedConfiguration.isCurrentlyPending) {
            return false;
        }

        return true;
    }

    _showDiagnosticsLink(store: MobxTeamStore): boolean {
        return store.user.isSupportPersonnel;
    }

    _showNotificationMenu(store: MobxTeamStore): boolean {
        return (store.masterAccountId !== null);
    }

    _getCssTab(tab: PageTabs): string {
        return `header-nav__${tab}`;
    }

    _getCssActive(store: MobxTeamStore, tab: PageTabs): string {
        return `${store.currentPageTab === tab ? 'active' : ''}`;
    }

    _getCssActiveTab(store: MobxTeamStore, tab: PageTabs): string {
        return `pxl-link ${this._getCssTab(tab)} ${this._getCssActive(store, tab)}`;
    }

    render(): React.ReactNode {
        const { store, isFullPageHelpForm } = this.props;
        if (isFullPageHelpForm || store?.hideHeaderForPdf()) {
            return null;
        }
        if (!store) {
            return (
                <header className="header-nav">
                    <nav className="header-nav__bar">
                        <div className="header-nav__logo">
                            <button
                                className="header-nav__brand pxl-link"
                                type="button"
                            >
                                <img src={require('contents/payscale_logo_white.svg')} className="header-nav__img" alt=""/>
                            </button>
                        </div>
                    </ nav>
                </ header>
            );
        }
        return (
            <header className="header-nav">
                <nav className="header-nav__bar">
                    <div className="header-nav__logo">
                        <button
                            className="header-nav__brand pxl-link"
                            onClick={this.goToLandingPage}
                            type="button"
                        >
                            <img src={require('contents/payscale_logo_white.svg')} className="header-nav__img" alt=""/>
                        </button>
                    </div>
                    <div className="header-nav__main">
                        <div
                            className="pxl-link header-nav__hide-notification"
                            onClick={notificationStore.hideNotificationMenu}
                            role="presentation"
                        >
                            <ul className="header-nav__main-list">
                                <li className="header-nav__overview-link" hidden={!this._showOverviewTab(store)}>
                                    <button
                                        className={this._getCssActiveTab(store, PageTabs.Overview)}
                                        onClick={this.goToOverview}
                                        type="button"
                                    >
                                    Overview
                                    </button>
                                </li>
                                {this._showTeamsTab(store) &&
                                (
                                    <li className="header-nav__teams-link">
                                        <button
                                            className={this._getCssActiveTab(store, PageTabs.Teams)}
                                            onClick={this.goToTeams}
                                            type="button"
                                        >
                                            Teams
                                        </button>
                                    </li>
                                )}
                                {this.renderEmployeesTab()}
                                {this.renderJobsTab()}
                                <li
                                    className="header-nav__settings-dropdown"
                                    hidden={!store.showMenuForRoles(Roles.HumanResource, Roles.Support)}
                                >
                                    <Dropdown
                                        btnText="Settings"
                                        id="settingDropdown"
                                        ref={(element: typeof Dropdown) => store.bindSettingsDropdown(element)}
                                    >
                                        <button
                                            className="pxl-dropdown__item header-settings__cycle-config"
                                            onClick={this.goToCycleConfiguration}
                                            type="button"
                                        >
                                            Cycle Configuration
                                        </button>
                                        <button
                                            className="pxl-dropdown__item header-settings__global-config"
                                            onClick={this.goToGlobalConfiguration}
                                            type="button"
                                        >
                                            Global Configuration
                                        </button>
                                        {this._showDiagnosticsLink(store) &&
                                        (
                                            <button
                                                className="pxl-dropdown__item header-settings__diagnostics-config"
                                                onClick={this.goToDiagnosticsPage}
                                                type="button"
                                            >
                                                Diagnostics
                                            </button>
                                        )}
                                    </Dropdown>
                                </li>
                            </ul>
                        </div>
                    </div>
                    {this.renderUserDisplayName()}
                    <div className="header-nav__secondary">
                        <ul className="header-nav__secondary-list">
                            {this._showNotificationMenu(store) &&
                            (
                                <li>
                                    <button
                                        className="header-nav__notification-count pxl-link"
                                        onClick={notificationStore.toggleNotificationMenu}
                                        type="button"

                                    >
                                        <i className="icon-bell" />
                                        <span hidden={notificationStore.count === 0}>{notificationStore.count}</span>
                                    </button>
                                </li>
                            )}
                            {this.showContactForm &&
                                <li>
                                    <button
                                        className="header-nav__contact-us-button pxl-link"
                                        onClick={this.toggleSlideout}
                                        type="button"
                                    >
                                        Contact Us
                                    </button>
                                </li>
                            }
                            <li>
                                <button
                                    className="header-nav__logout pxl-link"
                                    onClick={this.logout}
                                    type="button"
                                >
                                    Logout
                                </button>
                            </li>
                        </ul>
                    </div>
                </nav>
            </header>
        );
    }

    private goToOverview(): void {
        this.props.store?.goToOverview();
    }

    private goToCycleConfiguration(): void {
        this.props.store?.goToCycleConfiguration();
    }

    private logout(): void {
        this.props.store?.logout();
    }

    private goToGlobalConfiguration(): void {
        this.props.store?.goToGlobalConfiguration();
    }

    private goToTeams(): void {
        this.props.store?.goToTeams();
    }

    private goToLandingPage(): void {
        this.props.store?.goToLandingPage();
    }

    private goToJobs(): void {
        this.props.store?.goToJobs();
    }

    private goToEmployees(): void {
        this.props.store?.goToEmployees();
    }

    private toggleSlideout(): void {
        contactFormHelper.toggleSlideout();
    }

    private goToDiagnosticsPage(): void {
        return navigateToNoMst.navigateToDiagnostics();
    }

    private toggleNotificationMenu(): void {
        notificationStore.toggleNotificationMenu();
    }

    private renderUserDisplayName(): React.ReactNode {
        const user = this.props.store?.user;
        if (!user) {
            return null;
        }

        return (<div className="header-nav__username"><span>{user.displayName}</span></div>);
    }

    private renderJobsTab(): React.ReactNode {
        const { store } = this.props;
        if (!store || !this.showUnificationTabs) {
            return null;
        }

        let jobsLink = null;
        if (this.teamPayJobsList) {
            jobsLink = (
                <button
                    className={this._getCssActiveTab(store, PageTabs.Jobs)}
                    onClick={this.goToJobs}
                    type="button"
                >
                    Jobs
                </button>
            );
        } else {
            jobsLink = (
                <a
                    className={this._getCssActiveTab(store, PageTabs.Jobs)}
                    href={store.jobCollabJobsUrl}
                >
                    Jobs
                </a>
            );
        }

        return (
            <li className="header-nav__jobs-link">
                {jobsLink}
            </li>
        );
    }

    private renderEmployeesTab(): React.ReactNode {
        const { store } = this.props;
        if (!store || !this.showUnificationTabs) {
            return null;
        }

        let employeesLink = null;
        if (this.teamPayJobsList) {
            employeesLink = (
                <button
                    className={this._getCssActiveTab(store, PageTabs.Employees)}
                    onClick={this.goToEmployees}
                    type="button"
                >
                    Employees
                </button>
            );
        } else {
            employeesLink = (
                <a
                    className={this._getCssActiveTab(store, PageTabs.Employees)}
                    href={store.jobCollabEmployeesUrl}
                >
                    Employees
                </a>
            );
        }

        return (
            <li className="header-nav__employees-link">
                {employeesLink}
            </li>
        );
    }

    private handleKeyPress(e: React.KeyboardEvent<HTMLDivElement>): void {
        if (e.key === 'Enter') {
            notificationStore.hideNotificationMenu();
        }
    }
}

export default inject('store')(observer(HeaderNav));
